/**
 * E57 · Portal navigation & caseload — THE CLIENT TOOL CATALOGUE (registry `docs/em&es/epics/E57-portal-navigation.yaml`,
 * decisions `groups`, `key-map`, `names`; founder Q-pia-2 A, Q-pia-6 A, Q-e57g1-1b A, 2026-10-17).
 *
 * One list, shared by the portal (the client page's A5 rows, My tools, the All records filter, the pickers), the API
 * (the `setHiddenTools` validator drops a key this list does not know) and the domain (a tool's "in use" signal and its
 * All records rows). The ORDER is the canonical group order — the same row for every client (Q-e57g1-1b A): My tools
 * decides which pills exist, "in use" only styles them.
 *
 * Per tool (the registry key map): the `?tab=` key (= the All records key), the testid suffix of its client-page pill
 * (`cc-client-show-tab-<testId>` — the twelve shipped ones never change, 19 Playwright specs use them), the legacy
 * `/tools/*` list it replaces (that address opens All records with this tool ticked), the create page that keeps
 * `?clientId=`, and the homework target types that put it in use. Sessions & notes is row 1's own entry, not a tool of
 * a group, so it is not in this list (`SESSIONS_KEY`).
 */
import { ASSESSMENT_INSTRUMENT_IDS } from './db';

export const CLIENT_TOOL_GROUPS = [
  'plan',
  'track',
  'formulation',
  'skills',
  'safety',
] as const;
export type ClientToolGroup = (typeof CLIENT_TOOL_GROUPS)[number];

/**
 * E57 · D-nav-fixes-2026-10-19 (founder Q-nav-5 B): the small group labels inside Skills & exercises' second row — Cognitive ·
 * Behavioral · Values & motivation · Mindfulness, in this order (Meditations after Motivation). Labels:
 * `clientPage.skillsSubgroup.<key>`. A Skills tool without one is listed after the labelled ones.
 */
export const SKILLS_SUBGROUPS = ['cognitive', 'behavioral', 'valuesMotivation', 'mindfulness'] as const;
export type SkillsSubgroup = (typeof SKILLS_SUBGROUPS)[number];

export type ClientToolDef = {
  /** `?tab=` key, All records key, My tools key. */
  key: string;
  group: ClientToolGroup;
  /** The pill's testid suffix — `cc-client-show-tab-<testId>`. */
  testId: string;
  /** The legacy `/tools/<resource>` list this tool's All records filter replaces (Q-pia-3 A). */
  legacyResource?: string;
  /** The tool's own create page; its Create opens it with `?clientId=` (Q-e57g1-2 A rule 4; Use a tool removed, Q-nav-6 A). */
  createPath?: string;
  /** `HomeworkAssigned.target.type` values (kind `tool`) / `target.ref` (kind `assessment`) that put the tool in use. */
  homeworkTargets: readonly string[];
  /** Homework KINDS that belong to this tool (the Homework tool: reading · task · link · video; worksheets: worksheet). */
  homeworkKinds?: readonly string[];
  /**
   * The client's own work behind the VIEW grant (F-196, P020): for a client who does not share view, the client page reads
   * none of its records (E57 B5) and its section says "not shared" in one line. Absent = the PRACTICE's own record
   * (homework, worksheets, conceptualizations — B5), counted and shown whatever the client shares.
   */
  ownWork?: boolean;
  /** Skills & exercises only: the small label it sits under in the row (Q-nav-5 B). */
  subgroup?: SkillsSubgroup;
};

export const CLIENT_TOOLS: readonly ClientToolDef[] = [
  // ── Plan ──
  {
    key: 'homework',
    group: 'plan',
    testId: 'homework',
    homeworkTargets: [],
    homeworkKinds: ['reading', 'task', 'link', 'video'],
  },
  {
    key: 'worksheets',
    group: 'plan',
    testId: 'worksheets',
    homeworkTargets: [],
    homeworkKinds: ['worksheet'],
  },
  // ── Track ──
  {
    key: 'assessments',
    group: 'track',
    testId: 'assessments',
    legacyResource: 'portalTests',
    homeworkTargets: [...ASSESSMENT_INSTRUMENT_IDS],
    homeworkKinds: ['assessment'],
    ownWork: true,
  },
  { key: 'checkins', group: 'track', testId: 'checkins', homeworkTargets: ['checkIn'], ownWork: true },
  { key: 'diarycard', group: 'track', testId: 'diarycard', homeworkTargets: ['diaryCard'], ownWork: true },
  { key: 'sleep-diary', group: 'track', testId: 'sleep-diary', homeworkTargets: ['sleepDiary'], ownWork: true },
  { key: 'health', group: 'track', testId: 'health', homeworkTargets: [], ownWork: true },
  // ── Formulation ──
  {
    key: 'conceptualizations',
    group: 'formulation',
    testId: 'conceptualizations',
    legacyResource: 'conceptualizations',
    createPath: '/tools/conceptualizations/create',
    homeworkTargets: [],
  },
  {
    key: 'vicious-cycles',
    group: 'formulation',
    testId: 'vicious-cycles',
    legacyResource: 'supportingCycles',
    createPath: '/tools/supportingCycles/create',
    homeworkTargets: ['viciousCycle'],
    ownWork: true,
  },
  {
    key: 'functional-analysis',
    group: 'formulation',
    testId: 'functional-analysis',
    homeworkTargets: ['chainAnalysis'],
    ownWork: true,
  },
  { key: 'modes', group: 'formulation', testId: 'modes', homeworkTargets: ['modeCheckIn'], ownWork: true },
  // ── Skills & exercises ──
  {
    key: 'thought-tests',
    group: 'skills',
    testId: 'thought-tests',
    legacyResource: 'portalThoughts',
    createPath: '/tools/portalThoughts/create',
    homeworkTargets: ['thoughtTest'],
    subgroup: 'cognitive',
    ownWork: true,
  },
  {
    key: 'abc-models',
    group: 'skills',
    testId: 'abc-models',
    legacyResource: 'abcModels',
    createPath: '/tools/abcModels/create',
    homeworkTargets: ['abcModel'],
    subgroup: 'cognitive',
    ownWork: true,
  },
  {
    key: 'downward-arrows',
    group: 'skills',
    testId: 'downward-arrows',
    legacyResource: 'downwardArrows',
    createPath: '/tools/downwardArrows/create',
    homeworkTargets: ['downwardArrow'],
    subgroup: 'cognitive',
    ownWork: true,
  },
  {
    key: 'worry-trees',
    group: 'skills',
    testId: 'worry-trees',
    legacyResource: 'worryTrees',
    createPath: '/tools/worryTrees/create',
    homeworkTargets: ['worryTree'],
    subgroup: 'cognitive',
    ownWork: true,
  },
  {
    key: 'coping-cards',
    group: 'skills',
    testId: 'coping-cards',
    legacyResource: 'portalCards',
    createPath: '/tools/portalCards/create',
    homeworkTargets: [],
    subgroup: 'cognitive',
    ownWork: true,
  },
  {
    key: 'behavioral-activation',
    group: 'skills',
    // the shipped testid kept `activate` when the tab was renamed (2026-10-11) — unchanged (key map)
    testId: 'activate',
    homeworkTargets: ['activityMonitoring', 'activityRanking', 'activityPlanning', 'supportContract'],
    subgroup: 'behavioral',
    ownWork: true,
  },
  {
    key: 'behavior-experiments',
    group: 'skills',
    testId: 'behavior-experiments',
    legacyResource: 'behaviorExperiments',
    createPath: '/tools/behaviorExperiments/create',
    homeworkTargets: ['behaviorExperiment'],
    subgroup: 'behavioral',
    ownWork: true,
  },
  {
    key: 'exposures',
    group: 'skills',
    testId: 'exposures',
    legacyResource: 'exposures',
    createPath: '/tools/exposures/create',
    homeworkTargets: ['exposure'],
    subgroup: 'behavioral',
    ownWork: true,
  },
  {
    key: 'values',
    group: 'skills',
    testId: 'values',
    homeworkTargets: ['valuesSort', 'valuesWheel', 'valuedLivingQuestionnaire', 'windRose'],
    subgroup: 'valuesMotivation',
    ownWork: true,
  },
  // E49 — Motivation (E57 reserved the place: "Motivation with E49"; D-e57-reconcile-2026-10-18); since D-nav-fixes-2026-10-19
  // (Q-nav-5 B) it sits beside Values under "Values & motivation", and Meditations follows it under "Mindfulness"
  {
    key: 'motivation',
    group: 'skills',
    testId: 'motivation',
    homeworkTargets: ['changeRating', 'decisionalBalance', 'changePlan'],
    subgroup: 'valuesMotivation',
    ownWork: true,
  },
  {
    key: 'meditations',
    group: 'skills',
    testId: 'meditations',
    homeworkTargets: ['meditation'],
    subgroup: 'mindfulness',
    ownWork: true,
  },
  // ── Safety ──
  { key: 'safetyplan', group: 'safety', testId: 'safetyplan', homeworkTargets: [], ownWork: true },
];

/** The tools whose records are the PRACTICE's own (E57 B5) — what the client page counts for a client who does not share view. */
export const PRACTICE_RECORD_TOOLS: readonly string[] = CLIENT_TOOLS.filter((t) => !t.ownWork).map((t) => t.key);

/** Row 1's own entries beside the five groups. */
export const OVERVIEW_KEY = 'overview';
export const SESSIONS_KEY = 'sessions';

export const CLIENT_TOOL_KEYS: readonly string[] = CLIENT_TOOLS.map((t) => t.key);

export const isClientToolKey = (key: unknown): key is string =>
  typeof key === 'string' && CLIENT_TOOL_KEYS.includes(key);

export const clientToolOf = (key: string | null | undefined): ClientToolDef | undefined =>
  key ? CLIENT_TOOLS.find((t) => t.key === key) : undefined;

export const clientToolsOfGroup = (group: ClientToolGroup): ClientToolDef[] =>
  CLIENT_TOOLS.filter((t) => t.group === group);

/** The legacy `/tools/<resource>` list → its tool key (old addresses open All records pre-filtered, Q-pia-3 A). */
export const clientToolOfLegacyResource = (resource: string): ClientToolDef | undefined =>
  CLIENT_TOOLS.find((t) => t.legacyResource === resource);

/**
 * A homework assignment → the tool it puts in use (key map: "Homework target types to tool keys"), or null.
 * kind `tool` maps its `target.type`; kind `assessment` its instrument (`target.ref`); the plain kinds
 * (reading · task · link · video) and worksheet map to the Homework / Worksheets tools themselves.
 */
export const clientToolOfHomework = (
  kind: string | null | undefined,
  target: { type?: string | null; ref?: string | null } | null | undefined,
): string | null => {
  if (!kind) return null;
  const byKind = CLIENT_TOOLS.find((t) => t.homeworkKinds?.includes(kind));
  if (byKind) return byKind.key;
  const type = target?.type ?? null;
  if (!type) return null;
  return CLIENT_TOOLS.find((t) => t.homeworkTargets.includes(type))?.key ?? null;
};

/**
 * The stored HIDDEN set, normalised: known keys only (a key the catalogue does not know is dropped at the edge — the
 * board's SetHiddenTools), each once, in the canonical order.
 */
export const normaliseHiddenTools = (keys: readonly unknown[] | null | undefined): string[] => {
  const asked = new Set((keys ?? []).filter(isClientToolKey));
  return CLIENT_TOOL_KEYS.filter((k) => asked.has(k));
};
